// agentModeRuntime.js — THE ONE RUNTIME behind Agent mode (WS-AGENT-MODE-1).
//
// The TUI's apps, data backend and state machine are ONE source,
// `@symbo.ls/workspace-model` (smbls; the terminal TUI imports the same
// package). This file does exactly three things and nothing else:
//   1. create the headless runtime from the shell's OWN SDK instance and
//      session (user, org, workspace); re-create it on a workspace switch;
//   2. hand its `snapshot()` to the frame (components/AgentModeFrame.js) —
//      every render reads the snapshot only;
//   3. forward the person's input: keys (`handle`), clicks (`click`) and native
//      typing (`input`).
// No app, no data call and no state transition lives here.
//
// WHERE THINGS LIVE. The runtime is not state: it sits on the root as a plain
// `__` property (`root.__agentModeRuntime`, like `root.__navbarSaveQueue` —
// the store keeps `__` keys out of its signals). A render of the runtime bumps
// ONE declared root key, `_agentModeRev`; every frame factory reads it, so a
// change re-runs exactly the factories that read the snapshot. The snapshot of
// a revision is computed once and cached beside the runtime.
//
// THE HOST (what a runtime cannot do alone; the contract at the top of the
// package's runtime):
//   open(url)            leave Agent mode, then route the shell to that path
//   exit()               leave Agent mode (`q`, the frame's Leave control)
//   switchWorkspace(id, w)  the header switcher's own commit (shared
//                        AppOrgSwitcher `commitOrgSwitch`): a pick in another
//                        org goes through switchOrg, the same loader, deadline,
//                        URL and failure toast as the header; the runtime is
//                        re-created when root.workspace changes
//   edit                 absent: an editor field is a multiline field here
//
// Frank note: every export is self-contained (FA204); the package is a
// dynamic import inside the handler (FA206), so a shell that never enters
// Agent mode never loads it.

// The workspace id the runtime belongs to ('' before the workspace lands).
export const agentModeWorkspaceId = (root) => {
  const w = root && root.workspace
  return String((w && (w.id || w._id)) || '')
}

// Start, keep or re-create the runtime for the shell's current state. Called
// by the frame at mount and whenever Agent mode or the workspace changes.
export async function agentModeRuntimeEnsure() {
  const el = this
  const root = el && el.state && el.state.root
  if (!root) return null
  const shown = root.agentMode === true || root._agentModeLink === true
  const wsId = String((root.workspace && (root.workspace.id || root.workspace._id)) || '')
  const current = root.__agentModeRuntime || null
  if (!shown || !wsId) {
    if (current) el.call('agentModeRuntimeStop')
    return null
  }
  if (current && root.__agentModeRuntimeWs === wsId) {
    // The shell's language changed: the model speaks it from its next render
    // (the package's `setLang`; an older package without it stays English).
    const lang = String(root.lang || 'en')
    if (root.__agentModeLang !== lang && typeof current.setLang === 'function') {
      root.__agentModeLang = lang
      current.setLang(lang)
    }
    return current
  }
  if (root.__agentModeStarting && root.__agentModeStartingWs === wsId)
    return root.__agentModeStarting
  // Start on the full workspace record only. At boot (and on a switch) the
  // shell holds a list entry first (`_shape: 'list'`, shared
  // functions/activeWorkspaceRecord.js) — no `type`, no settings — and the
  // model keeps the session it starts with: the header read
  // "system--hq.workspace" where the TUI reads "system.workspace" (dev
  // 2026-10-23: list at 2.1 s, full at 3.0 s, both under the boot screen).
  // The frame's stateDeps run this again when the full record lands; a
  // record without the marker starts at once.
  if (root.workspace && root.workspace._shape === 'list') return null
  if (current) el.call('agentModeRuntimeStop')
  const gen = (root.__agentModeGen || 0) + 1
  root.__agentModeGen = gen
  root.__agentModeStartingWs = wsId
  const bump = () => {
    if (root.__agentModeGen !== gen) return
    root.update({ _agentModeRev: (Number(root._agentModeRev) || 0) + 1 }, { preventFetch: true })
  }
  const leave = () => {
    if (typeof el.call === 'function') el.call('setAgentMode', false)
  }
  const host = {
    open: (url) => {
      let path = '/'
      try {
        const u = new URL(String(url || '/'), 'https://agent.mode.invalid')
        path = `${u.pathname}${u.search}${u.hash}`
      } catch (_) {
        path = '/'
      }
      leave()
      if (typeof el.router === 'function') el.router(path, el.getRoot())
    },
    exit: () => leave(),
    // `w` is the picked row ({ id, name, org: { id, slug, name } }). The
    // header switcher's commit is reached through the component registry,
    // the way app.js reads the same object's `activeOrg`. sdk.switchWorkspace
    // alone never moves the org: a pick in another org did nothing (dev
    // 2026-10-23), so it is only the fallback without the shared switcher.
    switchWorkspace: (id, w) => {
      const comps = el.context && el.context.components
      const internals = comps && comps.__orgSwitcherInternals
      const o = w && w.org
      const orgId = o && (o.id || o._id)
      if (internals && typeof internals.commitOrgSwitch === 'function' && orgId) {
        const org = { ...o, id: orgId }
        return internals.commitOrgSwitch(el, root, org, id, (w && w.name) || o.name || '')
      }
      const sdk = el.sdk || (el.context && el.context.sdk)
      return sdk && typeof sdk.switchWorkspace === 'function'
        ? sdk.switchWorkspace(id)
        : Promise.resolve(null)
    }
  }
  const starting = (async () => {
    try {
      const mod =
        el.context && el.context.agentModeModel
          ? el.context.agentModeModel
          : await import('@symbo.ls/workspace-model')
      const sdk = el.sdk || (el.context && el.context.sdk)
      const data =
        el.context && el.context.agentModeData
          ? el.context.agentModeData
          : await mod.createSdkBackend({
              sdk,
              // The session the shell already holds: nothing is resolved
              // again and the SDK context is not changed (README).
              session: {
                user: {
                  id: root.currentUserId || null,
                  email: root.currentUserEmail || null,
                  username: root.currentUserUsername || null
                },
                org: root.org || null,
                workspace: root.workspace || null,
                workspaces:
                  Array.isArray(root.workspaces) && root.workspaces.length
                    ? root.workspaces
                    : undefined
              }
            })
      if (root.__agentModeGen !== gen) return null
      // The model speaks the shell's language (the package's `lang` option:
      // app names, hints, help, dialogs, dates).
      const lang = String(root.lang || 'en')
      const runtime = mod.createWorkspaceRuntime({ data, host, options: { shellUrl: '', lang } })
      root.__agentModeLang = lang
      root.__agentModeModule = mod
      root.__agentModeRuntime = runtime
      root.__agentModeRuntimeWs = wsId
      root.__agentModeSnapAt = -1
      runtime.on('render', bump)
      runtime.start()
      root.update({ _agentModeError: null }, { preventFetch: true })
      bump()
      return runtime
    } catch (err) {
      if (root.__agentModeGen !== gen) return null
      console.warn('[shell] agent mode could not start:', (err && err.message) || err)
      root.update({ _agentModeError: String((err && err.message) || err) }, { preventFetch: true })
      return null
    } finally {
      if (root.__agentModeGen === gen) {
        root.__agentModeStarting = null
        root.__agentModeStartingWs = null
      }
    }
  })()
  root.__agentModeStarting = starting
  return starting
}

// Stop the runtime (Agent mode left, the frame removed, a workspace switch).
export function agentModeRuntimeStop() {
  const root = this && this.state && this.state.root
  if (!root) return
  const runtime = root.__agentModeRuntime
  root.__agentModeGen = (root.__agentModeGen || 0) + 1
  root.__agentModeRuntime = null
  root.__agentModeModule = null
  root.__agentModeRuntimeWs = null
  root.__agentModeLang = null
  root.__agentModeSnap = null
  root.__agentModeSnapAt = -1
  root.__agentModeStarting = null
  if (runtime && typeof runtime.stop === 'function') {
    try {
      runtime.stop()
    } catch (err) {
      console.warn('[shell] agent mode stop failed:', (err && err.message) || err)
    }
  }
}

// The snapshot of the current revision, or null before the runtime runs.
// Reads `_agentModeRev` FIRST, so every caller re-runs on a runtime render.
export const agentModeSnapshot = (root) => {
  if (!root || typeof root !== 'object') return null
  const rev = Number(root._agentModeRev) || 0
  const runtime = root.__agentModeRuntime
  if (!runtime || typeof runtime.snapshot !== 'function') return null
  if (root.__agentModeSnapAt !== rev || !root.__agentModeSnap) {
    root.__agentModeSnap = runtime.snapshot()
    root.__agentModeSnapAt = rev
  }
  return root.__agentModeSnap
}

// Forward one TUI key event. Answers true when it reached the runtime.
export function agentModeHandle(ev) {
  const root = this && this.state && this.state.root
  const runtime = root && root.__agentModeRuntime
  if (!runtime || !ev) return false
  runtime.handle(ev)
  return true
}

// The keydown bridge on the frame: KeyboardEvent → runtime.handle. A key the
// runtime takes is the frame's — the browser's default (scroll, focus move,
// typing a '/') and every shell listener above the frame are kept out.
//
// The KeyboardEvent → key mapping is the package's own (`keyFromDom`), and so
// is the rule for a text control (`isControlKey`: a field types natively and
// forwards only enter, esc, tab, ↑↓, page keys, ctrl/⌘ K and S). Two browser
// rules are this renderer's: a ctrl/⌘ chord other than the frame's own
// (K palette, S save, N P list moves) stays the browser's (reload, new tab,
// address bar), and a multiline field keeps its Enter and arrows.
export function agentModeKeydown(e) {
  const root = this && this.state && this.state.root
  // Ctrl+` — the workspace or its CLI (functions/workspaceMode.js): back to the
  // workspace, before the runtime reads the key.
  if (
    e &&
    (e.ctrlKey || e.metaKey) &&
    !e.altKey &&
    (e.key === '`' || e.code === 'Backquote') &&
    this &&
    typeof this.call === 'function'
  ) {
    this.call('setAgentMode', false)
    if (typeof e.preventDefault === 'function') e.preventDefault()
    if (typeof e.stopPropagation === 'function') e.stopPropagation()
    return true
  }
  const mod = root && root.__agentModeModule
  if (!mod || typeof mod.keyFromDom !== 'function') return false
  const ev = mod.keyFromDom(e)
  if (!ev) return false
  if (ev.ctrl && !['k', 's', 'n', 'p'].includes(ev.name)) return false
  const target = e && e.target
  const tag = target && target.tagName ? String(target.tagName).toLowerCase() : ''
  const inField = tag === 'input' || tag === 'textarea' || tag === 'select'
  // A field the model has already left takes no native typing: the key is
  // the runtime's, in order, as in the TUI (esc then q: q quits).
  const stale = inField && this.call('agentModeFieldStale', target)
  if (!stale) {
    if (tag === 'textarea' && !ev.ctrl && ['enter', 'up', 'down'].includes(ev.name)) return false
    if (inField && !mod.isControlKey(ev)) return false
  }
  if (!this.call('agentModeHandle', ev)) return false
  if (typeof e.preventDefault === 'function') e.preventDefault()
  if (typeof e.stopPropagation === 'function') e.stopPropagation()
  return true
}

// The frame's document listener (AgentModeFrame `onDocumentKeydown`): a key
// that no element holds. CLI mode is the whole shell, so a key that lands on
// the page body is the frame's — the frame takes the focus back and the key
// goes through its bridge. Measured on dev 2026-10-23: the boot screen
// (index.html lockBodyInput) keeps every body child `inert` for ~3 s after the
// frame mounts, every focus() in that time is a no-op, and after the lock
// lifted the keys reached nobody (the shell's shortcut guard waits while an
// aria-modal frame is shown) until a click. A key on any other element (a
// toast's button, a shell dialog over the frame) stays that element's, and
// nothing runs while the boot screen still locks the page.
export function agentModeDocumentKeydown(e) {
  const el = this
  const node = el && el.node
  if (!e || e.defaultPrevented || !node || !node.isConnected) return false
  const target = e.target
  if (target && target !== node.ownerDocument.body && target !== node.ownerDocument.documentElement)
    return false
  if (typeof node.closest === 'function' && node.closest('[inert]')) return false
  if (typeof node.focus === 'function') node.focus({ preventScroll: true })
  return el.call('agentModeKeydown', e)
}

// A frame field the browser still focuses but the model has left. The
// runtime moves on synchronously (esc closes the picker, tab goes to the next
// form field); the DOM follows at the next render, one task later. A key in
// that gap went into the stale field as text and was lost (dev 2026-10-23:
// a q pressed right after esc landed in the closing picker's filter, and CLI
// mode stayed open). The model's focus is read FRESH here: the cached
// snapshot of the revision is the stale one.
export function agentModeFieldStale(node) {
  const root = this && this.state && this.state.root
  const runtime = root && root.__agentModeRuntime
  if (!runtime || typeof runtime.snapshot !== 'function') return false
  if (!node || typeof node.getAttribute !== 'function') return false
  const own = node.getAttribute('data-agent-target')
  if (!own) return false
  return this.call('agentModeFocusTarget', runtime.snapshot()) !== own
}

// A click on a hit zone: `payload` is what the TUI's zones carry ({ app },
// { tab }, { col, row }, { sel }, { select }, { ask }, { write }, overlay
// { pick } { field } { submit } { ok }). A second click of a pair opens.
export function agentModeClick(payload, opts) {
  const root = this && this.state && this.state.root
  const runtime = root && root.__agentModeRuntime
  if (!runtime || !payload) return false
  runtime.click(payload, { double: !!(opts && opts.double) })
  return true
}

// Native typing in a frame field: the model stays the source of truth.
export function agentModeInput(target, value, caret) {
  const root = this && this.state && this.state.root
  const runtime = root && root.__agentModeRuntime
  if (!runtime || !target) return false
  return runtime.input(
    target,
    String(value == null ? '' : value),
    Number.isInteger(caret) ? { caret } : {}
  )
}
